import { useState, useEffect } from 'react';
import { getMortgage, getMortgagePayments, deleteMortgagePayment } from '../api';
import { styles } from '../styles';
import { Spinner } from '../components/Icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

function fmt(val) {
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function buildChartData(schedule, actualBalances) {
  // Full lookup so any date can get its expected balance
  const scheduleByDate = {};
  schedule.forEach(entry => {
    scheduleByDate[entry.date] = Math.round(Number(entry.expected_balance));
  });

  const points = {};

  // Sampled schedule points (every 6 months) for the expected line
  schedule.forEach((entry, i) => {
    if (i % 6 === 0 || i === schedule.length - 1) {
      points[entry.date] = {
        rawDate: entry.date,
        date: fmtDate(entry.date),
        expected: Math.round(Number(entry.expected_balance)),
      };
    }
  });

  // Actual balance points — always included, always with expected filled in
  actualBalances.forEach(a => {
    const expected = scheduleByDate[a.date];
    if (points[a.date]) {
      points[a.date].actual = Math.round(a.balance);
    } else {
      points[a.date] = {
        rawDate: a.date,
        date: fmtDate(a.date),
        expected: expected,
        actual: Math.round(a.balance),
      };
    }
  });

  return Object.values(points)
    .sort((a, b) => a.rawDate.localeCompare(b.rawDate))
    .map(({ rawDate, ...rest }) => rest);
}

export default function MortgageScreen({ onBack, onLogPayment, onSetup }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    getMortgage()
      .then(d => { setData(d); setLoading(false); })
      .catch(err => {
        if (err.status === 404) {
          setData(null);
          setError(null);
        } else {
          setError('Failed to load mortgage data');
        }
        setLoading(false);
      });
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    setDeleting(id);
    try {
      await deleteMortgagePayment(id, false);
      load();
    } catch {
      alert('Failed to delete payment');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div style={{ ...styles.screen, ...styles.centered }}>
        <Spinner />
      </div>
    );
  }

  const noMortgage = !data;

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}><BackIcon /></button>
        <h1 style={styles.headerTitle}>Mortgage</h1>
        <button style={screenStyles.setupBtn} onClick={onSetup}>
          {noMortgage ? 'Set Up' : 'Settings'}
        </button>
      </header>

      {error && <p style={{ color: '#ef4444', textAlign: 'center' }}>{error}</p>}

      {noMortgage ? (
        <div style={screenStyles.emptyState}>
          <div style={screenStyles.emptyIcon}>🏠</div>
          <p style={screenStyles.emptyTitle}>No mortgage configured</p>
          <p style={screenStyles.emptySubtitle}>Set up your mortgage to track your balance and payments.</p>
          <button style={screenStyles.primaryBtn} onClick={onSetup}>Get Started</button>
        </div>
      ) : (
        <>
          {/* Summary card */}
          <div style={screenStyles.summaryCard}>
            <div style={screenStyles.summaryRow}>
              <div style={screenStyles.summaryItem}>
                <span style={screenStyles.summaryLabel}>Current Balance</span>
                <span style={screenStyles.summaryValue}>{fmt(data.current_balance)}</span>
              </div>
              <div style={screenStyles.summaryItem}>
                <span style={screenStyles.summaryLabel}>Monthly Payment</span>
                <span style={screenStyles.summaryValue}>{fmt(data.next_scheduled_payment)}</span>
              </div>
            </div>
            <div style={screenStyles.summaryRow}>
              <div style={screenStyles.summaryItem}>
                <span style={screenStyles.summaryLabel}>Original Principal</span>
                <span style={screenStyles.summaryValueSm}>{fmt(data.mortgage.original_principal)}</span>
              </div>
              <div style={screenStyles.summaryItem}>
                <span style={screenStyles.summaryLabel}>Payments Made</span>
                <span style={screenStyles.summaryValueSm}>{data.payments_made} / {data.mortgage.term_months}</span>
              </div>
            </div>
            <div style={screenStyles.summaryRow}>
              <div style={screenStyles.summaryItem}>
                <span style={screenStyles.summaryLabel}>Current Rate</span>
                <span style={screenStyles.summaryValueSm}>{(Number(data.current_rate) * 100).toFixed(2)}%</span>
              </div>
              <div style={screenStyles.summaryItem}>
                <span style={screenStyles.summaryLabel}>Projected Payoff</span>
                <span style={screenStyles.summaryValueSm}>{fmtDate(data.projected_payoff_date)}</span>
              </div>
            </div>
            {data.next_renewal_date && (() => {
              const renewalDate = new Date(data.next_renewal_date + 'T00:00:00');
              const today = new Date();
              const daysUntil = Math.ceil((renewalDate - today) / (1000 * 60 * 60 * 24));
              const isAmber = daysUntil <= 90;
              return (
                <div style={{
                  ...screenStyles.summaryRow,
                  backgroundColor: isAmber ? '#fef3c7' : '#f8fafc',
                  borderRadius: 8,
                  padding: '8px 10px',
                  marginTop: -4,
                }}>
                  <div style={screenStyles.summaryItem}>
                    <span style={{ ...screenStyles.summaryLabel, color: isAmber ? '#92400e' : '#94a3b8' }}>
                      {isAmber ? '⚠ Renewal Coming' : 'Renews'}
                    </span>
                    <span style={{ ...screenStyles.summaryValueSm, color: isAmber ? '#92400e' : '#475569' }}>
                      {fmtDate(data.next_renewal_date)}
                      {isAmber && ` (${daysUntil}d)`}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Balance chart */}
          {data.schedule && data.schedule.length > 0 && (
            <div style={screenStyles.chartCard}>
              <h3 style={screenStyles.sectionTitle}>Balance Over Time</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart
                  data={buildChartData(data.schedule, data.actual_balances)}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                    width={48}
                  />
                  <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, '']} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey="expected"
                    name="Expected"
                    stroke="#94a3b8"
                    dot={false}
                    strokeWidth={1.5}
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    name="Actual"
                    stroke="#6366f1"
                    dot={false}
                    strokeWidth={2}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Log payment button */}
          <button style={screenStyles.primaryBtn} onClick={onLogPayment}>
            Log Payment
          </button>

          {/* Recent payments */}
          {data.payments_made > 0 && (
            <section style={{ marginTop: 24 }}>
              <h3 style={screenStyles.sectionTitle}>Payment History</h3>
              <div style={screenStyles.paymentList}>
                {/* We need to call the payments endpoint separately; use schedule data for now */}
                <PaymentListLoader
                  mortgageId={data.mortgage.id}
                  onDelete={handleDelete}
                  deleting={deleting}
                />
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function PaymentListLoader({ onDelete, deleting }) {
  const [payments, setPayments] = useState(null);

  useEffect(() => {
    getMortgagePayments().then(setPayments).catch(() => setPayments([]));
  }, []);

  if (!payments) return <div style={{ textAlign: 'center', padding: 16 }}><Spinner /></div>;
  if (payments.length === 0) return null;

  return payments.map(p => (
    <div key={p.id} style={screenStyles.paymentRow}>
      <div style={screenStyles.paymentLeft}>
        <span style={screenStyles.paymentDate}>{fmtDate(p.payment_date)}</span>
        <span style={screenStyles.paymentBreakdown}>
          Principal: ${Number(p.principal_paid).toFixed(2)} · Interest: ${Number(p.interest_paid).toFixed(2)}
        </span>
        {p.notes && <span style={screenStyles.paymentNotes}>{p.notes}</span>}
      </div>
      <div style={screenStyles.paymentRight}>
        <span style={screenStyles.paymentTotal}>${Number(p.total_payment).toFixed(2)}</span>
        <span style={screenStyles.paymentBalance}>bal {fmt(p.balance_after)}</span>
        <button
          style={screenStyles.deleteBtn}
          onClick={() => onDelete(p.id)}
          disabled={deleting === p.id}
        >
          ✕
        </button>
      </div>
    </div>
  ));
}

const screenStyles = {
  setupBtn: {
    fontSize: 14,
    fontWeight: 500,
    color: '#6366f1',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 0',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 60,
    gap: 12,
  },
  emptyIcon: { fontSize: 48 },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: '#1e293b', margin: 0 },
  emptySubtitle: { fontSize: 14, color: '#64748b', textAlign: 'center', maxWidth: 260, margin: 0 },
  summaryCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: '16px 20px',
    border: '1px solid #e2e8f0',
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  summaryLabel: {
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  summaryValue: {
    fontSize: 22,
    fontWeight: 700,
    color: '#1e293b',
  },
  summaryValueSm: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1e293b',
  },
  chartCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: '16px 12px',
    border: '1px solid #e2e8f0',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#475569',
    margin: '0 0 12px 4px',
  },
  primaryBtn: {
    width: '100%',
    padding: '14px 0',
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 8,
  },
  paymentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  paymentRow: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  paymentLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  paymentDate: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1e293b',
  },
  paymentBreakdown: {
    fontSize: 12,
    color: '#64748b',
  },
  paymentNotes: {
    fontSize: 12,
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  paymentRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  paymentTotal: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1e293b',
  },
  paymentBalance: {
    fontSize: 11,
    color: '#94a3b8',
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    fontSize: 12,
    padding: '2px 4px',
    marginTop: 4,
  },
};
